import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface Step {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  current: boolean;
}

interface StepProgressIndicatorProps {
  steps: Step[];
  className?: string;
}

export const StepProgressIndicator = ({ steps, className }: StepProgressIndicatorProps) => {
  return (
    <div className={cn("w-full max-w-3xl mx-auto", className)}>
      <div className="flex items-center justify-center relative px-4">
        {/* Progress line */}
        <div className="absolute top-5 left-0 right-0 h-px bg-white/30 z-0">
          <div
            className="h-full gradient-brand transition-all duration-500 ease-out"
            style={{
              width: `${(steps.filter(s => s.completed).length / (steps.length - 1)) * 100}%`
            }}
          />
        </div>

        {/* Steps container */}
        <div className="flex items-center justify-between w-full relative z-10">
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center">
              {/* Step indicator */}
              <div
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ease-out",
                  step.completed
                    ? "bg-primary text-primary-foreground shadow-sm shadow-primary/20"
                    : step.current
                      ? "bg-background border-2 border-primary text-primary shadow-sm shadow-primary/10"
                      : "bg-background/70 border-2 border-white/30 text-muted-foreground hover:border-white/50"
                )}
              >
                {step.completed ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>

              {/* Step title - only on larger screens */}
              <div className="mt-2 text-center hidden sm:block">
                <div
                  className={cn(
                    "text-[0.8rem] font-semibold tracking-[0.02em] transition-colors duration-300",
                    step.current || step.completed ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {step.title}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
